import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import QRCode from 'qrcode';
import { LinkSimple, Copy, Printer, Prohibit, ShareNetwork } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { plannerApi } from '../plannerApi';
import { jstDate } from '@/utils/jstDate';

// ガント公開共有 — リンク＋QRコード（ログイン不要の読み取り専用ページ /g/{token}）
export default function ShareGanttDialog({ open, onClose, kind, targetId, title }) {
  const [token, setToken] = useState(null);
  const [info, setInfo] = useState(null);
  const [days, setDays] = useState(30);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [qr, setQr] = useState('');

  useEffect(() => {
    if (!open || !targetId) return;
    setToken(null); setQr('');
    plannerApi.getShare(kind, targetId).then((d) => { setToken(d.token); setInfo(d); }).catch(() => {});
  }, [open, kind, targetId]);

  const url = token ? `${window.location.origin}/g/${token}` : '';

  useEffect(() => {
    if (!url) { setQr(''); return; }
    QRCode.toDataURL(url, { width: 480, margin: 1 }).then(setQr).catch(() => {});
  }, [url]);

  const issue = async () => {
    setLoading(true);
    try {
      const d = await plannerApi.createShare(kind, targetId,
        { days: Number(days), password: password || null });
      setToken(d.token); setInfo(d);
      toast.success(password ? '共有リンクを発行しました（パスワード付き）' : '共有リンクを発行しました');
    } catch (e) { toast.error(e?.response?.data?.detail || '発行に失敗しました'); }
    finally { setLoading(false); }
  };

  const revoke = async () => {
    if (!window.confirm('共有を停止しますか？既存のリンク・QRコードは無効になります。')) return;
    setLoading(true);
    try {
      await plannerApi.revokeShare(kind, targetId);
      setToken(null);
      toast.success('共有を停止しました');
    } catch (e) { toast.error(e?.response?.data?.detail || '停止に失敗しました'); }
    finally { setLoading(false); }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast.success('リンクをコピーしました'); }
    catch { toast.error('コピーに失敗しました'); }
  };

  const printQr = () => {
    const w = window.open('', '_blank', 'width=480,height=640');
    if (!w) return;
    w.document.write(`<html><head><title>ガント共有QR</title><style>
      body{font-family:sans-serif;text-align:center;padding:24px}
      img{width:70mm;height:70mm}
      h2{font-size:16px;margin:8px 0} p{font-size:11px;color:#666;word-break:break-all}
    </style></head><body>
      <h2>${(title || 'ガントチャート').replace(/</g, '&lt;')}</h2>
      <img src="${qr}" alt="QR" />
      <p>${url}</p>
      <script>window.onload=()=>{window.print();}</script>
    </body></html>`);
    w.document.close();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" aria-describedby={undefined} data-testid="gantt-share-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShareNetwork size={18} weight="duotone" className="text-indigo-600" />
            ガントチャートを共有
          </DialogTitle>
        </DialogHeader>
        <p className="text-xs text-zinc-500 -mt-1">
          リンクを知っている人は<span className="font-semibold text-zinc-700">ログインなし</span>で閲覧できます（読み取り専用・タイトルと日程のみ）。
        </p>
        {!token && (
          <div className="space-y-2 mb-3">
            <label className="block text-xs text-zinc-600">
              有効期限
              <select value={days} onChange={(e) => setDays(e.target.value)}
                data-testid="gantt-share-days"
                className="mt-1 w-full border border-zinc-300 rounded px-2 py-1.5 text-sm bg-white">
                <option value={7}>7日</option>
                <option value={30}>30日（既定）</option>
                <option value={90}>90日</option>
              </select>
            </label>
            <label className="block text-xs text-zinc-600">
              パスワード（任意・8文字以上）
              <input value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="gantt-share-password" placeholder="付けない場合は空欄"
                className="mt-1 w-full border border-zinc-300 rounded px-2 py-1.5 font-mono text-sm" />
            </label>
          </div>
        )}
        {token ? (
          <div className="space-y-4">
            <div>
              <p className="text-xs font-semibold text-zinc-600 mb-1 flex items-center gap-1"><LinkSimple size={13} /> 共有リンク</p>
              <div className="flex gap-2">
                <Input value={url} readOnly className="h-10 font-mono text-xs" data-testid="gantt-share-url" />
                <Button variant="outline" className="h-10 shrink-0" onClick={copy} data-testid="gantt-share-copy">
                  <Copy size={15} className="mr-1" /> コピー
                </Button>
              </div>
            </div>
            {qr && (
              <div className="flex flex-col items-center gap-2">
                <img src={qr} alt="共有QR" className="w-44 h-44 rounded-xl border border-zinc-200 p-2 bg-white" data-testid="gantt-share-qr" />
                <Button variant="outline" size="sm" onClick={printQr} data-testid="gantt-share-qr-print">
                  <Printer size={14} className="mr-1" /> QRを印刷
                </Button>
              </div>
            )}
            {info && (
              <p className="text-[11px] text-zinc-500" data-testid="gantt-share-info">
                有効期限 {jstDate(info.expires_at)}
                {info.has_password ? ' ・ パスワードあり' : ' ・ パスワードなし'}
                {typeof info.view_count === 'number' ? ` ・ 閲覧 ${info.view_count}回` : ''}
              </p>
            )}
            <Button variant="outline" onClick={revoke} disabled={loading}
              className="w-full text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="gantt-share-revoke">
              <Prohibit size={15} className="mr-1" /> 共有を停止（リンクを無効化）
            </Button>
          </div>
        ) : (
          <Button onClick={issue} disabled={loading} className="w-full bg-teal-700 hover:bg-teal-800 h-11" data-testid="gantt-share-issue">
            <ShareNetwork size={16} className="mr-1.5" /> {loading ? '発行中…' : '共有リンクを発行'}
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
}
